import { createContext, useContext, useState, useEffect, PropsWithChildren } from "react";
import { useParams, Outlet } from "react-router-dom";

import { getSetBySlug } from "./api/api.ts";
import { AppConfigurationContext } from './appConfigurationProvider.tsx';
import { LoadingBanner, LoadingState } from './components/loadingBanner.tsx';
import { BeamlineProvider } from './beamlineProvider.tsx';
import { SampleSetsProvider } from './sampleSetsProvider.tsx';


// The only purpose of this component is to accept a Set slug as a url parameter
// and provide the corredponging ESAF slug to a SampleSetsProvider.
// It's so we can respond to urls like /set/:setSlug, in addition to the more standard
// /esaf/:esafSlug/set/:setSlug.


enum EsafSlugLoadingState { NotTriggered, Pending, Succeeded, Failed };


const SampleSetsFromSet: React.FC<PropsWithChildren<{}>> = (props) => {

  let { setSlug: setSlugFromParams } = useParams();

  const [setSlug, setSetSlug] = useState<string | undefined>(setSlugFromParams);
  const [esafSlug, setEsafSlug] = useState<string | undefined>(undefined);
  const [loadingState, setLoadingState] = useState<EsafSlugLoadingState>(EsafSlugLoadingState.NotTriggered);
  const [bannerState, setBannerState] = useState<LoadingState>(LoadingState.Loading);
  const [bannerMessage, setBannerMessage] = useState<string>("Fetching Set record...");

  const appConfig = useContext(AppConfigurationContext);


  useEffect(() => {
    setLoadingState(EsafSlugLoadingState.Pending);
  }, []);


  useEffect(() => {

    const fetchData = async () => {
      try {
        if (setSlug === undefined) { throw new Error("Set Slug not defined"); }

        const p = setSlug.trim()
        if (!p) {
          setBannerMessage("Not Set ID provided in URL.");
          setBannerState(LoadingState.Failure);
          setLoadingState(EsafSlugLoadingState.Failed);
          return;
        }
        const setResponse = await getSetBySlug(setSlug);
        if (!setResponse.success || !setResponse.response) {
          setBannerMessage("Set ID not found.");
          setBannerState(LoadingState.Failure);
          setLoadingState(EsafSlugLoadingState.Failed);
          return;
        }
        const esafSlug = setResponse.response.slug_esaf;
        setEsafSlug(esafSlug);
        setLoadingState(EsafSlugLoadingState.Succeeded);
        return;
      } catch (error) {
        appConfig.log('Error fetching Set record:', error);
      }
      setLoadingState(EsafSlugLoadingState.Failed);
    };

    // Call fetchData when the loadingState changes
    if (loadingState == EsafSlugLoadingState.Pending) {
      fetchData();
    }
  }, [loadingState]);


  // If we're in any loading state other than success,
  // display a loading banner instead of the content.
  if (loadingState != EsafSlugLoadingState.Succeeded) {
    return (<LoadingBanner state={bannerState} message={bannerMessage}></LoadingBanner>)
  }


  return (
      <BeamlineProvider esafSlug={esafSlug}>
        <SampleSetsProvider esafSlug={esafSlug}>
          <Outlet />
        </SampleSetsProvider>
      </BeamlineProvider>
  )
}

export { SampleSetsFromSet }